import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [restaurantSlug, setRestaurantSlug] = useState(null);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        setCartItems(parsed.items || []);
        setRestaurantSlug(parsed.restaurantSlug || null);
      } catch (e) {
        console.error('Failed to load cart:', e);
      }
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    if (cartItems.length > 0 || restaurantSlug) {
      localStorage.setItem('cart', JSON.stringify({
        items: cartItems,
        restaurantSlug
      }));
    }
  }, [cartItems, restaurantSlug]);

  const addToCart = (item) => {
    // Item structure:
    // {
    //   id: menuItemId,
    //   name: string,
    //   basePrice: number,
    //   quantity: number,
    //   modifiers: [{ groupId, groupName, options: [{ id, name, price }] }],
    //   specialInstructions: string,
    //   totalPrice: number
    // }
    
    const cartItem = {
      ...item,
      cartItemId: `${item.id}-${Date.now()}-${Math.random()}`, // Unique cart item ID
    };
    
    setCartItems(prev => [...prev, cartItem]);
  };

  const removeFromCart = (cartItemId) => {
    setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    
    setCartItems(prev =>
      prev.map(item => {
        if (item.cartItemId === cartItemId) {
          // Recalculate total: (base price + modifiers) * quantity
          const modifierTotal = item.modifiers?.reduce((sum, group) => {
            return sum + group.options.reduce((optSum, opt) => optSum + opt.price, 0);
          }, 0) || 0;
          
          const itemPrice = item.basePrice + modifierTotal;
          const newTotalPrice = itemPrice * quantity;
          
          return { 
            ...item, 
            quantity, 
            totalPrice: newTotalPrice 
          };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setRestaurantSlug(null);
    localStorage.removeItem('cart');
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.totalPrice, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    restaurantSlug,
    setRestaurantSlug,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
